import { ComponentType } from '@angular/cdk/portal';
import { Injectable, OnDestroy } from '@angular/core';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface DialogOptions {
  navigationPath?: string;
  data?: Record<string, any>;
  cleanupFn?: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class DialogService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private dialogStack: MatDialogRef<any, any>[] = [];
  private currentDialog?: MatDialogRef<any, any>;
  private dialogState$ = new Subject<boolean>();

  constructor(private dialog: MatDialog, private router: Router) {}

  /**
   * Observable that emits when dialog state changes
   * true when any dialog is open, false when all dialogs are closed
   */
  get dialogStateChanges$(): Observable<boolean> {
    return this.dialogState$.asObservable();
  }

  /**
   * Opens a dialog without result handling
   * @param component Component to render in dialog
   * @param config Optional dialog configuration
   * @throws Error if dialog fails to open
   */
  openDialog<T, R = unknown>(
    component: ComponentType<T>,
    config: MatDialogConfig<unknown> = {}
  ): void {
    try {
      const dialogRef = this.dialog.open<T, unknown, R>(component, {
        disableClose: false,
        ...config,
      });
      this.dialogStack.push(dialogRef);
      this.currentDialog = dialogRef;
      this.dialogState$.next(true);
    } catch (error) {
      throw new Error(
        'Failed to open dialog: ' +
          (error instanceof Error ? error.message : String(error))
      );
    }
  }

  /**
   * Opens a dialog and returns an Observable of the result
   * @param component Component to render in dialog
   * @param config Optional dialog configuration
   * @returns Observable of dialog result
   * @throws Error if dialog fails to open
   */
  openDialogWithResult<T, R = unknown>(
    component: ComponentType<T>,
    config: MatDialogConfig<unknown> = {}
  ): Observable<R | undefined> {
    this.openDialog(component, config);
    const dialogRef = this.getCurrentDialog<T, R>();
    if (!dialogRef) {
      throw new Error('Failed to open dialog');
    }
    return dialogRef.afterClosed().pipe(takeUntil(this.destroy$));
  }

  /**
   * Closes current dialog and opens a new one
   * @param nextComponent Component to render in new dialog
   * @param config Optional dialog configuration
   * @param result Optional result to pass to closing dialog
   */
  async closeAndOpenDialog<T, R = unknown>(
    nextComponent: ComponentType<T>,
    config: MatDialogConfig<unknown> = {},
    result?: R
  ): Promise<void> {
    await this.safelyCloseDialog(result);
    this.openDialog<T, R>(nextComponent, config);
  }

  /**
   * Closes current dialog with a result
   * @param result Result to pass to closing dialog
   * @param options Optional dialog options for navigation/cleanup
   */
  async closeWithResult<R>(result: R, options?: DialogOptions): Promise<void> {
    await this.safelyCloseDialog(result, options);
  }

  /**
   * Closes current dialog without result
   * @param options Optional dialog options for navigation/cleanup
   */
  async close(options?: DialogOptions): Promise<void> {
    await this.safelyCloseDialog(undefined, options);
  }

  /**
   * Checks if any dialog is currently open
   * @returns True if dialog stack is not empty
   */
  hasOpenDialog(): boolean {
    return this.dialogStack?.length > 0;
  }

  /**
   * Gets the currently active dialog reference
   * @returns Current dialog reference or undefined
   */
  getCurrentDialog<T, R>(): MatDialogRef<T, R> | undefined {
    return this.currentDialog as MatDialogRef<T, R>;
  }

  /**
   * Gets readonly copy of dialog stack
   * @returns Array of dialog references
   */
  getDialogStack(): ReadonlyArray<MatDialogRef<any, any>> {
    return [...this.dialogStack];
  }

  /**
   * Closes all open dialogs
   * Cleans up dialog stack and references
   */
  async closeAll(): Promise<void> {
    try {
      for (let i = this.dialogStack.length - 1; i >= 0; i--) {
        const dialogRef = this.dialogStack[i];
        this.currentDialog = dialogRef;
        await this.safelyCloseDialog().catch((error) => {
          console.error('Error closing dialog:', error);
        });
      }
    } finally {
      this.dialogStack = [];
      this.currentDialog = undefined;
      this.dialog.closeAll();
    }
  }

  /**
   * Checks if a specific component dialog is currently open
   * @param component The component type to check for
   * @returns True if the specified component dialog is open
   */
  isDialogOpen<T>(component: ComponentType<T>): boolean {
    return this.dialogStack.some(
      (dialog) => dialog.componentInstance instanceof component
    );
  }

  async ngOnDestroy(): Promise<void> {
    await this.closeAll();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async safelyCloseDialog<R>(
    result?: R,
    options?: DialogOptions
  ): Promise<void> {
    if (!this.currentDialog) return;

    const dialogToClose = this.currentDialog;

    try {
      dialogToClose.close(result);
      this.dialogStack.pop();
      this.currentDialog =
        this.dialogStack[this.dialogStack.length - 1] || null;

      await dialogToClose
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .toPromise();

      // Emit new dialog state
      this.dialogState$.next(this.dialogStack.length > 0);

      if (options?.cleanupFn) {
        options.cleanupFn();
      }

      if (options?.navigationPath) {
        await this.router.navigate([options.navigationPath], {
          state: options.data || null,
        });
      }
    } catch (error) {
      console.error('Error in safelyCloseDialog:', error);
      throw error;
    }
  }
}
